import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRqStatus, RqStatusResult, RollupStatus } from './useAdminStatus';

interface RqHealthWidgetProps {
    viewerToken?: string;
}

// Formats an age in seconds as a short human readable duration
const formatAge = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}u`;
};

// Builds a short reason line explaining a non ok status
const buildReason = (rqStatus: RqStatusResult, t: (key: string, options?: object) => string) => {
    // Report an unreachable redis first
    if (rqStatus.error === 'redis_unreachable') {
        return t('dashboard.monitoring.admin.rq.redis_unreachable');
    }

    // Report the uncovered queues of both groups
    const uncovered = Object.values(rqStatus.groups).flatMap(group => group.uncovered_queues);
    if (uncovered.length > 0) {
        return t('dashboard.monitoring.admin.rq.uncovered_queues', { queues: uncovered.slice(0, 3).join(', ') });
    }

    // Report the queue with the oldest waiting job above the limit
    const staleQueue = rqStatus.queues
        .filter(queue => queue.oldest_age_seconds !== null && queue.oldest_age_seconds > 900)
        .sort((a, b) => (b.oldest_age_seconds || 0) - (a.oldest_age_seconds || 0))[0];
    if (staleQueue) {
        return t('dashboard.monitoring.admin.rq.stale_queue', {
            queue: staleQueue.name,
            depth: staleQueue.depth,
            age: formatAge(staleQueue.oldest_age_seconds || 0),
        });
    }

    // Report an unhealthy worker as a fallback
    const unhealthy = rqStatus.workers.find(worker => !worker.healthy);
    if (unhealthy) {
        return t('dashboard.monitoring.admin.rq.unhealthy_worker', { name: unhealthy.name });
    }

    return null;
};

const RqHealthWidget: React.FC<RqHealthWidgetProps> = ({ viewerToken }) => {
    const { t } = useTranslation();
    const { rqStatus, loading } = useRqStatus(viewerToken);

    // Fall back to critical when the endpoint gave no status yet
    const status: RollupStatus = rqStatus.status || 'critical';

    // Determine the covered state per queue group
    const workerCovered = rqStatus.groups.worker?.covered ?? false;
    const syncworkerCovered = rqStatus.groups.syncworker?.covered ?? false;

    // Count the total waiting jobs over every queue
    const totalDepth = rqStatus.queues.reduce((sum, queue) => sum + queue.depth, 0);

    // Build the reason line for a non ok status
    const reason = status !== 'ok' && !loading ? buildReason(rqStatus, t) : null;

    // Renders one line with a colored dot and a label
    const renderLine = (ok: boolean, label: string, detail: string) => (
        <div className='admin-status-line'>
            <span className={`admin-status-dot ${ok ? 'admin-status-ok' : 'admin-status-critical'}`} />
            <span className='admin-status-line-label'>{label}</span>
            <span className='admin-status-line-detail'>{detail}</span>
        </div>
    );

    return (
        <div className={`dashboard-card admin-status-widget admin-status-widget-${loading ? 'loading' : status}`}>
            <div className='admin-status-header'>
                <h6 className='title'>{t('dashboard.monitoring.admin.rq.title')}</h6>
                {!loading && <span className={`admin-status-dot admin-status-${status}`} />}
            </div>
            {loading ? (
                <div className='admin-status-loading'><div className='loader' /></div>
            ) : (
                <div className='admin-status-body'>
                    <div className='admin-status-lines'>
                        {renderLine(
                            workerCovered,
                            t('dashboard.monitoring.admin.rq.worker_label'),
                            workerCovered ? t('dashboard.monitoring.admin.rq.running') : t('dashboard.monitoring.admin.rq.down')
                        )}
                        {renderLine(
                            syncworkerCovered,
                            t('dashboard.monitoring.admin.rq.syncworker_label'),
                            syncworkerCovered ? t('dashboard.monitoring.admin.rq.running') : t('dashboard.monitoring.admin.rq.down')
                        )}
                        {renderLine(
                            rqStatus.scheduler.alive,
                            t('dashboard.monitoring.admin.rq.scheduler_label'),
                            rqStatus.scheduler.alive ? t('dashboard.monitoring.admin.rq.running') : t('dashboard.monitoring.admin.rq.down')
                        )}
                    </div>
                    <div className='admin-status-footer'>
                        {reason ? (
                            <span className={`admin-status-reason admin-status-${status}`}>{reason}</span>
                        ) : (
                            <span className='admin-status-line-detail'>
                                {t('dashboard.monitoring.admin.rq.queued_jobs', { count: totalDepth })}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RqHealthWidget;
